// cspell: ignore Hrefs

export type SVGTreeSanitizeOptions = {
    /**
     * 是否移除非 `#` 开头的外部 `href` 与 `xlink:href`，默认为 `true`。
     */
    removeExternalHrefs?: boolean;

    /**
     * 是否移除包含 `@import` 或外部 `url()` 的 `<style>` 节点，默认为 `true`。
     */
    removeExternalStyleResources?: boolean;

    /**
     * 是否移除包含外部 `url()` 的 `style` 属性，默认为 `true`。
     */
    removeExternalStyleAttributeURLs?: boolean;
};

/**
 * 原地移除 SVG 素材中的固定危险内容，并按调用方策略处理外部资源。
 *
 * @param root - 已由调用方解析并校验的 SVG 根节点。
 * @param options - 三类外部资源的可选清洗策略；省略的配置默认开启。
 * @returns 清洗后的同一 SVG 根节点。
 */
export const sanitizeSVGTree = (root: SVGSVGElement, options: SVGTreeSanitizeOptions = {}): SVGSVGElement => {
    const {
        removeExternalHrefs = true,
        removeExternalStyleResources = true,
        removeExternalStyleAttributeURLs = true,
    } = options;

    root.querySelectorAll('script,foreignObject').forEach((node) => node.remove());

    // 仅在调用方禁止外部 style 资源时移除对应节点，保留 class 和 url(#...) 内部引用。
    if (removeExternalStyleResources) {
        root.querySelectorAll('style').forEach((node) => {
            const value = node.textContent ?? '';
            if (/@import\b/i.test(value) || /url\((?!["']?#)/i.test(value)) node.remove();
        });
    }

    [root, ...Array.from(root.querySelectorAll('*'))].forEach((node) => {
        for (const attr of Array.from(node.attributes)) {
            const name = attr.name.toLowerCase();

            // 事件属性属于公共危险内容，根节点与全部后代都必须无条件清理。
            if (name.startsWith('on')) {
                node.removeAttribute(attr.name);
                continue;
            }

            // 外部引用由调用方选择是否禁止，# 开头的同文档引用始终保留。
            if (
                removeExternalHrefs &&
                (name === 'href' || name === 'xlink:href') &&
                !attr.value.trim().startsWith('#')
            ) {
                node.removeAttribute(attr.name);
            }

            // 行内样式只按策略拦截外部 URL，不影响 url(#...) 内部资源。
            if (removeExternalStyleAttributeURLs && name === 'style' && /url\((?!["']?#)/i.test(attr.value)) {
                node.removeAttribute(attr.name);
            }
        }
    });

    return root;
};
